/**
 * Keyboard cheat-sheet overlay.
 *
 * Renders the entire keyboard schema, grouped by section, with platform-aware key
 * glyphs. Generated live from `keybindingRegistry` — never hand-maintained — so it
 * always reflects the real bindings. Lazy-mounted by `KeyboardHost` only while
 * open; toggled by `?` / `⌘/`.
 *
 * Grouping is by `group` alone, so one section mixes bindings from several
 * contexts and the same chord legitimately appears twice (⌘⌫ deletes a table row
 * and a tree node). Each row therefore prints its context as a scope caption;
 * `global` prints none, since it applies everywhere.
 */

import { FC, useMemo } from 'react';
import { Modal, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import FlexCol from '@/components/atoms/FlexCol';
import FlexBox from '@/components/atoms/FlexBox';
import { keybindingRegistry } from './keybindingRegistry';
import {
  keyboardOverlayActions,
  keyboardOverlayState,
} from './keyboardOverlayStore';
import { formatChord } from './formatChord';
import { getPlatform } from './platform';
import type { KbdContext, KbdPlatform, Keybinding } from './keybinding.types';

const { Text, Title } = Typography;

const SCOPE_LABEL: Record<KbdContext, string> = {
  global: '',
  form: 'in a form',
  table: 'in a table',
  field: 'in a field',
  editor: 'in the layout editor',
  search: 'in search',
  tree: 'in a tree node',
};

const GROUP_ORDER = [
  'Global',
  'Navigation',
  'Editing',
  'Actions',
  'Search & help',
];

function groupBindings(bindings: Keybinding[]): [string, Keybinding[]][] {
  const byGroup = new Map<string, Keybinding[]>();
  for (const b of bindings) {
    if (b.hiddenFromCheatSheet) continue;
    const existing = byGroup.get(b.group) ?? [];
    existing.push(b);
    byGroup.set(b.group, existing);
  }
  const ordered = GROUP_ORDER.filter((g) => byGroup.has(g));
  const rest = [...byGroup.keys()].filter((g) => !GROUP_ORDER.includes(g));
  return [...ordered, ...rest].map((g) => [g, byGroup.get(g) ?? []]);
}

function BindingRow({
  binding,
  platform,
}: {
  binding: Keybinding;
  platform: KbdPlatform;
}): JSX.Element {
  const scope = SCOPE_LABEL[binding.context];
  return (
    <FlexBox noGrow>
      <FlexBox gap={5} noGrow justifyStart>
        <Text>{binding.label}</Text>
        {scope !== '' && <Text type="secondary">{scope}</Text>}
      </FlexBox>
      <FlexBox gap={5} noGrow justifyEnd>
        {binding.chords.map((chord, i) => (
          <Text keyboard key={i}>
            {formatChord(chord, platform)}
          </Text>
        ))}
      </FlexBox>
    </FlexBox>
  );
}

export const CheatSheet: FC = () => {
  const snap = useSnapshot(keyboardOverlayState);
  const platform = getPlatform();
  const groups = useMemo(() => groupBindings(keybindingRegistry.all()), []);

  return (
    <Modal
      open={snap.cheatSheetOpen}
      onCancel={keyboardOverlayActions.closeCheatSheet}
      footer={null}
      title="Keyboard shortcuts"
      width={640}
    >
      <FlexCol>
        {groups.map(([group, bindings]) => (
          <FlexCol gap={5} key={group}>
            <Title level={5}>{group}</Title>
            {bindings.map((binding) => (
              <BindingRow
                binding={binding}
                platform={platform}
                key={binding.id}
              />
            ))}
          </FlexCol>
        ))}
      </FlexCol>
    </Modal>
  );
};
